import React from 'react'
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom'
import { HelmetProvider } from 'react-helmet-async'
import { Elements } from '@stripe/react-stripe-js'
import { AuthProvider } from './contexts/AuthContext'
import { AdminAuthProvider } from './contexts/AdminAuthContext'
import { DialogProvider } from './contexts/DialogContext'
import { stripePromise } from './lib/stripe'
import Header from './components/Layout/Header'
import Footer from './components/Layout/Footer'
import ScrollToTop from './components/ScrollToTop'
import ProtectedRoute from './components/ProtectedRoute'
import AdminProtectedRoute from './components/AdminProtectedRoute'
import Home from './pages/Home'
import About from './pages/About'
import InPersonTraining from './pages/InPersonTraining'
import OnlineTraining from './pages/OnlineTraining'
import MealPlan from './pages/MealPlan'
import Contact from './pages/Contact'
import BookConsultation from './pages/BookConsultation'
import ManageConsultation from './pages/ManageConsultation'
import StartingOverGuide from './pages/StartingOverGuide'
import StrongAgainEnrollment from './pages/StrongAgainEnrollment'
import PackageEnrollment from './pages/PackageEnrollment'
import CoachingPricing from './pages/CoachingPricing'
import Login from './pages/auth/Login'
import SignUp from './pages/auth/SignUp'
import Dashboard from './pages/Dashboard'
import MessageCassie from './pages/MessageCassie'
import RequestCheckIn from './pages/RequestCheckIn'
import DashboardSettings from './pages/DashboardSettings'
import UpcomingSessions from './pages/UpcomingSessions'
import PrivacyPolicy from './pages/PrivacyPolicy'
import TermsOfService from './pages/TermsOfService'
import MacroCalculator from './pages/MacroCalculator'
import ComingHomeToMe from './pages/ComingHomeToMe'
import AdminLogin from './pages/admin/AdminLogin'
import AdminDashboard from './pages/admin/AdminDashboard'
import AdminClients from './pages/admin/AdminClients'
import AdminSessions from './pages/admin/AdminSessions'
import AdminPackages from './pages/admin/AdminPackages'
import AdminSettings from './pages/admin/AdminSettings'
import VideoCall from './pages/admin/VideoCall'
import AdminUserDashboards from './pages/admin/AdminUserDashboards'
import AdminUserDashboard from './pages/admin/AdminUserDashboard'
import AdminClientPreview from './pages/admin/AdminClientPreview'
import AdminConsultations from './pages/admin/AdminConsultations'
import AdminCheckInRequests from './pages/admin/AdminCheckInRequests'
import AdminClientMessages from './pages/admin/AdminClientMessages'
import AdminProgressCheckIns from './pages/admin/AdminProgressCheckIns'
import WeeklyProgress from './pages/WeeklyProgress'
import ClientGuide from './pages/ClientGuide'
import CommunityCenter from './pages/community/CommunityCenter'
import AdminCommunity from './pages/admin/AdminCommunity'

function App() {
  return (
    <HelmetProvider>
    <Elements stripe={stripePromise}>
      <AuthProvider>
        <AdminAuthProvider>
          <DialogProvider>
          <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
            <ScrollToTop />
            <div className="App">
              <Header />
              <main>
                <Routes>
                  {/* Public Routes */}
                  <Route path="/" element={<Home />} />
                  <Route path="/about" element={<About />} />
                  <Route path="/private-training" element={<InPersonTraining />} />
                  <Route path="/online-training" element={<OnlineTraining />} />
                  <Route path="/meal-plan" element={<MealPlan />} />
                  <Route path="/contact" element={<Contact />} />
<Route path="/book-consultation" element={<BookConsultation />} />
<Route path="/manage-consultation" element={<ManageConsultation />} />
<Route path="/starting-over-guide" element={<StartingOverGuide />} />
<Route path="/strong-again/enroll" element={<StrongAgainEnrollment />} />
<Route path="/enroll/:packageKey" element={<PackageEnrollment />} />
<Route path="/coaching" element={<CoachingPricing />} />
<Route path="/macro-calculator" element={<MacroCalculator />} />
  
                  <Route path="/coming-home-to-me" element={<ComingHomeToMe />} />
                  <Route path="/login" element={<Login />} />
                  <Route path="/signup" element={<SignUp />} />
                  <Route path="/privacy" element={<PrivacyPolicy />} />
                  <Route path="/terms" element={<TermsOfService />} />
                  
                  {/* User Protected Routes */}
                  <Route path="/dashboard" element={
                    <ProtectedRoute>
                      <Dashboard />
                    </ProtectedRoute>
                  } />

                  <Route path="/dashboard/settings" element={
                    <ProtectedRoute>
                      <DashboardSettings />
                    </ProtectedRoute>
                  } />
                  <Route path="/dashboard/request-check-in" element={
                    <ProtectedRoute>
                      <RequestCheckIn />
                    </ProtectedRoute>
                  } />
                  <Route path="/dashboard/messages" element={
                    <ProtectedRoute>
                      <MessageCassie />
                    </ProtectedRoute>
                  } />
                  <Route path="/dashboard/progress" element={
                    <ProtectedRoute>
                      <WeeklyProgress />
                    </ProtectedRoute>
                  } />
                  <Route path="/dashboard/client-guide" element={
                    <ProtectedRoute>
                      <ClientGuide />
                    </ProtectedRoute>
                  } />
                  <Route path="/dashboard/sessions" element={
                    <ProtectedRoute>
                      <UpcomingSessions />
                    </ProtectedRoute>
                  } />
                  <Route path="/dashboard/community" element={
                    <ProtectedRoute>
                      <CommunityCenter />
                    </ProtectedRoute>
                  } />

                  {/* Admin Routes */}
                  <Route path="/admin/login" element={<AdminLogin />} />
                  <Route path="/admin/dashboard" element={
                    <AdminProtectedRoute>
                      <AdminDashboard />
                    </AdminProtectedRoute>
                  } />
                  <Route path="/admin/clients" element={
                    <AdminProtectedRoute>
                      <AdminClients />
                    </AdminProtectedRoute>
                  } />
                  <Route path="/admin/sessions" element={
                    <AdminProtectedRoute>
                      <AdminSessions />
                    </AdminProtectedRoute>
                  } />
                  <Route path="/admin/consultations" element={
                    <AdminProtectedRoute>
                      <AdminConsultations />
                    </AdminProtectedRoute>
                  } />
                  <Route path="/admin/check-in-requests" element={
                    <AdminProtectedRoute>
                      <AdminCheckInRequests />
                    </AdminProtectedRoute>
                  } />
                  <Route path="/admin/client-messages" element={
                    <AdminProtectedRoute>
                      <AdminClientMessages />
                    </AdminProtectedRoute>
                  } />
                  <Route path="/admin/progress-checkins" element={
                    <AdminProtectedRoute>
                      <AdminProgressCheckIns />
                    </AdminProtectedRoute>
                  } />
                  <Route path="/admin/packages" element={
                    <AdminProtectedRoute>
                      <AdminPackages />
                    </AdminProtectedRoute>
                  } />
                  <Route path="/admin/settings" element={
                    <AdminProtectedRoute>
                      <AdminSettings />
                    </AdminProtectedRoute>
                  } />
                  <Route path="/admin/video-call" element={
                    <AdminProtectedRoute>
                      <VideoCall />
                    </AdminProtectedRoute>
                  } />
                  <Route path="/admin/user-dashboards" element={
                    <AdminProtectedRoute>
                      <AdminUserDashboards />
                    </AdminProtectedRoute>
                  } />
                  <Route path="/admin/user-dashboard/:clientId" element={
                    <AdminProtectedRoute>
                      <AdminUserDashboard />
                    </AdminProtectedRoute>
                  } />
                  <Route path="/admin/community" element={
                    <AdminProtectedRoute>
                      <AdminCommunity />
                    </AdminProtectedRoute>
                  } />
                  <Route path="/admin/client-view" element={
                    <AdminProtectedRoute>
                      <AdminClientPreview />
                    </AdminProtectedRoute>
                  } />



                </Routes>
              </main>
              <Footer />
            </div>
          </Router>
          </DialogProvider>
        </AdminAuthProvider>
      </AuthProvider>
    </Elements>
    </HelmetProvider>
  )
}

export default App 
